import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from "./config";
import { stopDemoMode } from "./demoMode";
import { APP_ONLY_EVENT, APP_ONLY_PATH } from "./appOnly";
// AuthProvider steht ausserhalb des ThemeProviders — AppLoadingOverlay ist reines
// CSS ohne MUI-Abhaengigkeit und funktioniert deshalb auch hier.
import AppLoadingOverlay from "./design-system/components/AppLoadingOverlay";
import "./design-system/styles/tokens.css";
import "./design-system/styles/components.css";
import { EMPLOYEE_HOME_PATH, isEmployeeAccessLevel } from './utils/accessLevels';

export const AuthContext = createContext(null);

const SELECTED_LOCATION_STORAGE_KEY = 'selected_location_id';

const PUBLIC_ROUTES = new Set([
  '/',
  '/login',
  '/app-only',
  '/forget_password',
  '/reset_password',
  '/token_registration',
  '/registration/admin',
  '/registration',
  '/registration/success',
]);

const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationIdState] = useState(
    () => localStorage.getItem(SELECTED_LOCATION_STORAGE_KEY) || ''
  );
  // Mirrors selectedLocationId so syncLocations can read the current value without
  // listing it as a dependency. Otherwise the callback identity changes on every
  // location change and effects depending on it (e.g. Topbar) re-run in a loop.
  const selectedLocationIdRef = useRef(selectedLocationId);
  const [loading, setLoading] = useState(true);  // To handle initial loading state
  const [error, setError] = useState('');

  const setSelectedLocationId = useCallback((value) => {
    const nextValue = value ? String(value) : '';
    if (nextValue === selectedLocationIdRef.current) return;
    selectedLocationIdRef.current = nextValue;
    if (nextValue) {
      localStorage.setItem(SELECTED_LOCATION_STORAGE_KEY, nextValue);
    } else {
      localStorage.removeItem(SELECTED_LOCATION_STORAGE_KEY);
    }
    setSelectedLocationIdState(nextValue);
    window.dispatchEvent(new CustomEvent('timetab:location-changed', {
      detail: { locationId: nextValue },
    }));
  }, []);

  const syncLocations = useCallback((nextLocations = [], preferredLocationId = '') => {
    const normalizedLocations = Array.isArray(nextLocations)
      ? nextLocations.filter((location) => location?.is_active !== false)
      : [];
    setLocations(normalizedLocations);

    if (normalizedLocations.length === 0) {
      if (selectedLocationIdRef.current) {
        setSelectedLocationId('');
      }
      return;
    }

    const validIds = new Set(normalizedLocations.map((location) => String(location.id)));
    const storedLocationId = localStorage.getItem(SELECTED_LOCATION_STORAGE_KEY) || '';
    const preferredId = preferredLocationId ? String(preferredLocationId) : '';
    const nextLocationId = validIds.has(storedLocationId)
      ? storedLocationId
      : validIds.has(preferredId)
        ? preferredId
        : String(normalizedLocations[0].id);

    setSelectedLocationId(nextLocationId);
  }, [setSelectedLocationId]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('session_token');

    if (storedToken && storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      if (!PUBLIC_ROUTES.has(location.pathname)) {
        navigate('/login');
      }
    }
    setLoading(false);  // Set loading to false after initial auth check
  }, [navigate, location.pathname]);

  // Eine laufende Session ist am zentralen /api/web-Check haengengeblieben: Die
  // Rolle hat keinen Web-Zugang (mehr). Storage hat appOnly.js schon geraeumt.
  useEffect(() => {
    const handleAppOnly = () => {
      setUser(null);
      setLocations([]);
      selectedLocationIdRef.current = '';
      setSelectedLocationIdState('');
      navigate(APP_ONLY_PATH, { replace: true });
    };

    window.addEventListener(APP_ONLY_EVENT, handleAppOnly);
    return () => window.removeEventListener(APP_ONLY_EVENT, handleAppOnly);
  }, [navigate]);

  useEffect(() => {
    // Handle post-login redirection
    if (!loading && user) {
      if (['/', '/login', '/register'].includes(location.pathname)) {
        // Mitarbeitende direkt in den Schichtplan: /welcome ruft Planungs-
        // Endpunkte auf, die fuer User gesperrt sind.
        navigate(isEmployeeAccessLevel(user.access_level) ? EMPLOYEE_HOME_PATH : '/welcome');
      }
    }
  }, [user, loading, navigate, location.pathname]);

  const login = async (email, password) => {
    try {
      // console.log('Email:', email, 'Password:', password);
      const response = await fetch(`${API_BASE_URL}/api/web/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });
      // Body zuerst lesen: der Fehlerfall unterscheidet sich hier inhaltlich, nicht
      // nur im Status. Ohne das wuerde jeder Fehler zu "Inkorrekte Email oder Passwort".
      const data = await response.json().catch(() => ({}));

      // Rolle ohne Web-Zugang: kein Token, stattdessen der Verweis auf die App.
      if (response.status === 403 && data?.error === 'app_only') {
        navigate(APP_ONLY_PATH);
        return;
      }

      if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
      }
      // console.log('Server response:', data);
      if (data.session_token) {
        localStorage.setItem('session_token', data.session_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);  // Set user here only after token validation
        syncLocations(data.user?.locations || [], data.user?.selected_location_id);
      } else {
        throw new Error('Token not received');
      }
    } catch (error) {
      setError('Inkorrekte Email oder Passwort');
    }
  };

  const logout = useCallback(() => {
    stopDemoMode();
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    localStorage.removeItem(SELECTED_LOCATION_STORAGE_KEY);
    setUser(null);
    setLocations([]);
    selectedLocationIdRef.current = '';
    setSelectedLocationIdState('');
    navigate('/login');
  }, [navigate]);

  if (loading) return <AppLoadingOverlay open />;

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        login,
        logout,
        error,
        setError,
        locations,
        selectedLocationId,
        setSelectedLocationId,
        syncLocations,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
